// components/Charts/BarChart.jsx

import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { consolidateData } from '../../../services/dashboard/formatDataConsolidate';
import { forwardRef, useImperativeHandle, useRef } from 'react';

// Eliminadas interfaces ChartData y BarChartProps

// Plugin para mostrar valores encima de las barras
const valueOnTopPlugin = {
    id: "valueOnTop",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);

            meta.data.forEach((bar, index) => {
                const value = dataset.data[index];
                if (value == null) return;

                ctx.save();
                ctx.font = "bold 12px sans-serif";
                ctx.fillStyle = "#000"; 
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";

                const x = bar.x;
                const y = bar.y - 5; 

                // ==== Sombra / borde blanco (Outline) ====
                ctx.strokeStyle = "white";   
                ctx.lineWidth = 2;           
                ctx.strokeText(value, x, y); 

                ctx.fillText(value, x, y);
                ctx.restore();
            });
        });
    }
};

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    valueOnTopPlugin
);

// Eliminada la interfaz BarChartRef

const BarChart = forwardRef(({
    data,
    title = 'Indicadores de Ecoeficiencia por Mes',
    height = '500px',
    showTitle = true,
    consolidate = true
}, ref) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getCanvas: () => canvasRef.current,
        toggleLegend: (show) => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.options.plugins.legend.display = show;
                chartInstanceRef.current.update();
            }
        },
        getLegendData: () => {
            if (!processedData || !processedData.datasets) return [];
            return processedData.datasets.map((dataset) => {
                const value = dataset.data[0];
                const unit = dataset.unit || '';
                return {
                    label: dataset.label,
                    value: `${value != null ? value.toFixed(2) : ''} ${unit}`,
                    color: dataset.backgroundColor
                };
            });
        }
    }));

    // Validación temprana
    if (!data || !data.datasets || data.datasets.length === 0) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#666',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9',
                }}
            >
                <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
                <p style={{ fontSize: '16px', fontWeight: 500 }}>
                    No hay datos de indicadores disponibles
                </p>
                <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>
                    Por favor, verifica que existan reportes para este proyecto
                </p>
            </div>
        );
    }

    const processedData = consolidate ? consolidateData(data) : data;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                position: 'top',
                labels: {
                    boxWidth: 15,
                    padding: 15,
                    font: {
                        size: 11,
                    },
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => {
                            const value = dataset.data[0];
                            const unit = dataset.unit || '';

                            return {
                                text: `${dataset.label}: ${value != null ? value.toFixed(2) : ''} ${unit}`,
                                fillStyle: dataset.backgroundColor,
                                strokeStyle: dataset.borderColor,
                                lineWidth: dataset.borderWidth,
                                hidden: !chart.isDatasetVisible(i),
                                index: i
                            };
                        });
                    }
                },
            },
            title: {
                display: showTitle,
                text: title,
                font: {
                    size: 16,
                    weight: 'bold',
                },
                padding: {
                    bottom: 0
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#666',
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        const value = context.parsed.y;
                        const dataset = context.dataset;
                        const unit = dataset.unit || ''; 
                        return `${dataset.label}: ${value.toFixed(2)} ${unit}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Valores',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
            },
            x: {
                title: {
                    display: true,
                    text: '',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                grid: {
                    display: false,
                },
            },
        },
    };

    const baseHeight = parseInt(height.replace('px', '')) || 500;
    const legendItemHeight = 25; 
    const itemsPerRow = 2; 
    const legendRows = processedData ? Math.ceil(processedData.datasets.length / itemsPerRow) : 0;
    const dynamicLegendHeight = legendRows * legendItemHeight;
    const finalHeight = `${baseHeight + Math.max(0, dynamicLegendHeight - 50)}px`;

    return (
        <div style={{ height: finalHeight, width: '100%' }}>
            <Bar
                data={processedData}
                options={options}
                ref={(instance) => {
                    if (instance) {
                        canvasRef.current = instance.canvas;
                        chartInstanceRef.current = instance;
                    }
                }}
            />
        </div>
    );
});

export default BarChart;